import { useState, useRef, useMemo, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Code, Eye, BookMarked } from 'lucide-react';
import { Button } from '../ui/button';
import { ImageInsertDialog } from './ImageInsertDialog';

// Import custom Quill configuration (registers custom blots)
import './quillConfig';

/**
 * RichTextEditor - A Blogger-style WYSIWYG editor with inline citation support
 * 
 * Features:
 * - Clean, writing-focused interface
 * - Essential formatting tools
 * - Inline citation insertion
 * - Toggle between rich-text and HTML view
 * - Produces clean semantic HTML
 */
export const RichTextEditor = forwardRef(function RichTextEditor({ 
  value = '', 
  onChange, 
  placeholder = 'Start writing...',
  minHeight = '400px',
  className = '',
  // Citation support
  citations = [],
  onInsertCitation,
  showCitationButton = false
}, ref) {
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [editorValue, setEditorValue] = useState(value);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const quillRef = useRef(null);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    insertCitation: (citationIndex) => {
      insertCitationAtCursor(citationIndex);
    },
    getEditor: () => quillRef.current?.getEditor()
  }));

  // Sync external value changes
  useEffect(() => {
    if (value !== editorValue) {
      setEditorValue(value);
    }
  }, [value]);

  // Insert citation marker at cursor position
  const insertCitationAtCursor = useCallback((citationIndex) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection(true);
    if (range) {
      // Create citation HTML: <sup><a href="#ref-X" class="citation-marker" data-citation="X">X</a></sup>
      const citationNumber = citationIndex + 1;
      const citationHtml = `<sup><a href="#ref-${citationNumber}" class="citation-marker" data-citation="${citationNumber}">[${citationNumber}]</a></sup>`;
      
      // Insert the citation embed
      quill.clipboard.dangerouslyPasteHTML(range.index, citationHtml, 'user');
      quill.setSelection(range.index + 3, 0); // Move cursor after citation
    }
  }, []);

  // Insert HTML at cursor position
  const insertHtmlAtCursor = useCallback((html) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection(true);
    if (range) {
      // Insert a newline before and after the figure for proper spacing
      quill.clipboard.dangerouslyPasteHTML(range.index, '\n' + html + '\n', 'user');
      quill.setSelection(range.index + html.length + 2, 0);
    }
  }, []);

  // Handle image insert from dialog
  const handleImageInsert = useCallback((figureHtml) => {
    insertHtmlAtCursor(figureHtml);
  }, [insertHtmlAtCursor]);

  // Quill modules configuration - complete toolbar
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        // Row 1: History + Headings
        ['undo', 'redo'],
        [{ 'header': [2, 3, false] }],
        
        // Row 2: Text formatting
        ['bold', 'italic', 'underline'],
        ['clean'],
        
        // Row 3: Structure
        ['blockquote'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        
        // Row 4: Alignment
        [{ 'align': [] }],
        
        // Row 5: Media & Links
        ['link', 'customImage', 'divider']
      ],
      handlers: {
        'undo': function() {
          this.quill.history.undo();
        },
        'redo': function() {
          this.quill.history.redo();
        },
        'customImage': function() {
          // This will be overridden after mount
        },
        'divider': function() {
          const range = this.quill.getSelection(true);
          this.quill.insertText(range.index, '\n');
          this.quill.insertEmbed(range.index + 1, 'divider', true);
          this.quill.insertText(range.index + 2, '\n');
          this.quill.setSelection(range.index + 3);
        }
      }
    },
    history: {
      delay: 1000,
      maxStack: 100,
      userOnly: true
    },
    clipboard: {
      matchVisual: false // Produces cleaner HTML
    }
  }), []);

  // Quill formats - semantic tags + alignment
  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'blockquote',
    'list',
    'align',
    'link', 'image',
    'divider'
  ];

  // Override the customImage handler after mount
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const toolbar = quill.getModule('toolbar');
      if (toolbar) {
        toolbar.addHandler('customImage', () => {
          setShowImageDialog(true);
        });
      }
    }
  }, []);

  // Handle rich-text changes
  const handleQuillChange = (content) => {
    setEditorValue(content);
    if (onChange) {
      onChange(content);
    }
  };

  // Handle HTML mode changes
  const handleHtmlChange = (e) => {
    const newHtml = e.target.value;
    setEditorValue(newHtml);
    if (onChange) {
      onChange(newHtml);
    }
  };

  // Toggle between modes
  const toggleMode = () => {
    setIsHtmlMode(!isHtmlMode);
  };

  // Handle insert citation button click
  const handleInsertCitationClick = () => {
    if (onInsertCitation) {
      onInsertCitation();
    }
  };

  return (
    <div className={`rich-text-editor ${className}`} data-testid="rich-text-editor">
      {/* Mode Toggle & Citation Button */}
      <div className="flex items-center justify-between mb-2">
        <div>
          {showCitationButton && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleInsertCitationClick}
              className="text-xs"
              data-testid="insert-citation-btn"
            >
              <BookMarked className="h-3 w-3 mr-1" />
              Insert Citation
              {citations.length > 0 && (
                <span className="ml-1 bg-accent text-accent-foreground px-1.5 py-0.5 rounded-full text-[10px]">
                  {citations.length}
                </span>
              )}
            </Button>
          )}
        </div>
        <Button
          type="button"
          variant={isHtmlMode ? 'default' : 'ghost'}
          size="sm"
          onClick={toggleMode}
          className="text-xs"
          data-testid="toggle-html-mode"
        >
          {isHtmlMode ? (
            <>
              <Eye className="h-3 w-3 mr-1" />
              Rich Text
            </>
          ) : (
            <>
              <Code className="h-3 w-3 mr-1" />
              HTML
            </>
          )}
        </Button>
      </div>

      {/* Editor Container */}
      <div className="editor-container border border-border rounded-md overflow-hidden bg-background">
        {isHtmlMode ? (
          /* HTML Source View */
          <textarea
            value={editorValue}
            onChange={handleHtmlChange}
            placeholder="<p>Enter HTML here...</p>"
            className="w-full font-mono text-sm p-4 bg-slate-50 text-slate-800 focus:outline-none resize-none"
            style={{ minHeight }}
            data-testid="html-editor"
          />
        ) : (
          /* Rich Text Editor */
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={editorValue}
            onChange={handleQuillChange}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
            style={{ minHeight }}
          />
        )}
      </div>

      {/* Helper text */}
      <p className="text-xs text-muted-foreground mt-2">
        {isHtmlMode 
          ? 'Editing raw HTML. Supported tags: <h2>, <h3>, <p>, <strong>, <em>, <u>, <blockquote>, <ul>, <ol>, <li>, <a>, <img>, <figure>, <figcaption>, <hr>, <sup>'
          : showCitationButton 
            ? 'Use "Insert Citation" to add inline references. Click the image icon to add images with captions.'
            : 'Toolbar: Undo/Redo • Headings • Bold/Italic/Underline • Clear • Quote • Lists • Alignment • Links • Images (with caption) • Divider'
        }
      </p>

      {/* Image Insert Dialog */}
      <ImageInsertDialog
        open={showImageDialog}
        onOpenChange={setShowImageDialog}
        onInsert={handleImageInsert}
      />
    </div>
  );
});

export default RichTextEditor;
